// «Installate MK Client sul telefono»: chi lo vede, su che telefono è, e come si installa davvero.
//
// L'app aggiunta alla schermata Home non è un vezzo: su iPhone è l'UNICO modo perché arrivino le notifiche
// (utils/serviceWorker.js), e su tutti i telefoni è la differenza fra «mi ricordo il sito» e un'icona che si
// tocca. Per questo la proposta compare nella home del cliente e nella pagina di accesso, e resta sempre a
// portata nel Profilo.
//
// Le due strade sono diverse davvero, non per modo di dire:
// - Android/Chrome: il browser stesso offre l'installazione con l'evento `beforeinstallprompt`. Si intercetta
//   (il browser lo lancia una volta sola, spesso PRIMA che React sia in piedi: per questo si ascolta da
//   index.js) e al tocco del cliente si apre la finestra vera del sistema.
//   Se l'evento non arriva — browser che non lo sostiene, oppure Chrome che ha deciso di non offrirlo — si
//   ripiega sulla guida a mano, come su iPhone.
// - iPhone: `beforeinstallprompt` non esiste e non esisterà. Si installa solo a mano, da **Safari**:
//   Condividi › «Aggiungi alla schermata Home» › «Aggiungi». Da Chrome o Firefox su iPhone non si può proprio,
//   e la guida lo dice invece di far provare a vuoto.

import { appInstallata, suIPhone } from './serviceWorker';

export { appInstallata, suIPhone };

// Quanto resta nascosta la card dopo «Non ora». Sette giorni: abbastanza da non essere insistenti, poco
// abbastanza da riproporla a chi quel giorno aveva solo fretta.
export const GIORNI_NON_ORA = 7;
const CHIAVE_NON_ORA = 'mk_installa_non_ora';

export const rimandata = () => {
  try {
    return Number(localStorage.getItem(CHIAVE_NON_ORA) || 0) > Date.now();
  } catch {
    return false;   // finestra anonima, archivio bloccato: si mostra, non è un danno
  }
};

export const rimanda = () => {
  try {
    localStorage.setItem(CHIAVE_NON_ORA, String(Date.now() + GIORNI_NON_ORA * 24 * 60 * 60 * 1000));
  } catch { /* archivio non disponibile: pazienza, tornerà al prossimo caricamento */ }
};

export const dimenticaRinvio = () => {
  try { localStorage.removeItem(CHIAVE_NON_ORA); } catch { /* niente */ }
};

// ---------------------------------------------------------------- che telefono è

const ua = () => (typeof navigator !== 'undefined' && navigator.userAgent) || '';

export const suAndroid = () => /Android/i.test(ua());

// 'iphone' | 'android' | 'altro' (computer, o un telefono che non sappiamo riconoscere)
export const piattaforma = () => (suIPhone() ? 'iphone' : suAndroid() ? 'android' : 'altro');

export const suTelefono = () => suIPhone() || suAndroid();

// Su iPhone l'installazione la fa **solo** Safari. Chrome, Firefox, Edge e Opera per iOS sono Safari sotto il
// cofano, ma il menu «Aggiungi a Home» non ce l'hanno: si riconoscono dalla loro sigla nello user agent.
const SIGLE_ALTRI_IOS = /CriOS|FxiOS|EdgiOS|OPT\//;

// 'safari' | 'altro'. Ha senso solo su iPhone; altrove torna sempre 'safari' (nessun avviso da dare).
export const browserDiIPhone = () => {
  if (!suIPhone()) return 'safari';
  return SIGLE_ALTRI_IOS.test(ua()) ? 'altro' : 'safari';
};

// ---------------------------------------------------------------- l'invito di Chrome (Android)

// L'evento arriva una volta sola e va conservato: si usa quando il cliente tocca «Installa», non prima.
let invito = null;
let ascoltatori = [];
let giaInstallata = false;

const avvisa = () => ascoltatori.forEach((fn) => {
  try { fn(); } catch { /* un ascoltatore rotto non ferma gli altri */ }
});

/** Da chiamare una volta all'avvio (src/index.js): il browser lancia l'evento prestissimo. */
export const ascoltaInstallazione = () => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    // senza preventDefault Chrome mostra la sua barretta quando vuole lui: la mostriamo noi, dove serve
    if (typeof e.preventDefault === 'function') e.preventDefault();
    invito = e;
    avvisa();
  });
  window.addEventListener('appinstalled', () => {
    giaInstallata = true;
    invito = null;
    dimenticaRinvio();   // installata: il rinvio non serve più a niente
    avvisa();
  });
};

export const invitoPronto = () => Boolean(invito);

/** Chi vuole ridisegnarsi quando l'invito arriva (o quando l'app viene installata). Torna la disdetta. */
export const quandoCambia = (fn) => {
  ascoltatori.push(fn);
  return () => { ascoltatori = ascoltatori.filter((x) => x !== fn); };
};

/**
 * Apre la finestra di installazione del sistema.
 * Torna 'accettata' | 'rifiutata' | null (nessun invito da usare: tocca alla guida a mano).
 */
export const installaConInvito = async () => {
  if (!invito) return null;
  const e = invito;
  invito = null;          // l'invito si spende una volta sola
  try {
    await e.prompt();
    const esito = await e.userChoice;
    return esito && esito.outcome === 'accepted' ? 'accettata' : 'rifiutata';
  } catch {
    return null;          // il browser l'ha rifiutata (per esempio già consumata): si ripiega sulla guida
  } finally {
    avvisa();
  }
};

// ---------------------------------------------------------------- si mostra o no

export const installataAdesso = () => giaInstallata || appInstallata();

/**
 * La card «Installate l'app» si mostra a chi ce l'ha davanti: **su un telefono**, con l'app **non installata** e
 * senza un «Non ora» ancora valido. Su computer no: lì l'icona sulla scrivania non serve a nessuno, e le
 * notifiche arrivano lo stesso.
 */
export const daProporre = () => suTelefono() && !installataAdesso() && !rimandata();

/**
 * Cosa succede al tocco di «Installa»: 'invito' (la finestra di Chrome) oppure 'guida' (i passi a mano).
 * Su iPhone è sempre la guida; su Android dipende da cosa ha deciso il browser.
 */
export const comeInstallare = () => (invitoPronto() ? 'invito' : 'guida');

// Solo per i test: rimette il modulo com'era appena caricato
export const _azzera = () => { invito = null; ascoltatori = []; giaInstallata = false; };
